import * as Constants from '../../services/Constants'
import { useState } from 'react'
import { AudioInputSelection, AudioInputSettings, useUserContext } from '@/UserContext'
import useAudioSettingsStoreActions from '@/core/useAudioSettingsStoreActions'
import useUserContextRef from '@/services/useUserContextRef'
import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import ElectronIPC from '@/services/ElectronIPC'
import useAppContextRef from '@/services/useAppContextRef'
import { useTranslation } from 'react-i18next'
import { Button } from '@/UIComponents2/Button'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'
import InternalGuideIcon from './internal/icons/InternalGuideIcon'
import { DialogBody } from '@/components/Dialog'
import {
    ChannelSection,
    LatencySelectionSection,
    LoopbackToggleSection,
    MaxDriftSelectionSection,
    PacketSizeSelectionSection,
    QualitySelectionDescriptionSection,
    SampleRateSelectionSection,
} from './internal/Sections'
import { AudioQualitySelect } from '@/components/AudioQualitySelect'

export default function AudioInputDeviceSettingsContent({
    mediaSelection,
    onClose,
}: {
    mediaSelection: AudioInputSelection
    onClose: () => void
}) {
    const mediaSource = mediaSelection.source
    const settings = mediaSelection.settings

    const [userState] = useUserContext()
    const { appStateRef } = useAppContextRef()
    const { userStateRef } = useUserContextRef()

    const [currSettings, set_currSettings] = useState<AudioInputSettings>(settings)

    const { setAudioInputSettings } = useAudioSettingsStoreActions()

    const { toggleAudioDeviceSelection, updateSelectedAudioDeviceProperties, updateSelectedAudioDeviceQualityPreset } =
        useMediaSelectionActions()

    const { t } = useTranslation()

    const atrack = userState.mediaStreams.audioInputs.find((i) => i.info.deviceId === mediaSelection.deviceId) ?? null

    const showAdvanced = appStateRef.current.appState.isInDevMode || false

    const currQualityPresetCode = mediaSelection
        ? Constants.settingsToAudioQualityPesetCode(mediaSelection.settings)
        : 'voice'
    const handleChangeQuality = (code: Constants.AudioQualityPresetCode) => {
        if (mediaSelection) {
            updateSelectedAudioDeviceQualityPreset(mediaSelection, code)
        }
    }

    const isTalkback = userStateRef.current.mediaSelection.talkbackMic === mediaSelection.deviceId

    const setSettingsHelper = (param: keyof AudioInputSettings, value: string) => {
        if (param === 'isCompressed') {
            set_currSettings((s) => {
                return { ...s, isCompressed: JSON.parse(value) }
            })
        } else {
            set_currSettings((s) => {
                return { ...s, [param]: parseInt(value) }
            })
        }
    }

    const onSubmit = () => {
        // the below fields are the ones changed at "dialog ok" time instead
        // of at "real time"
        const settingsNext = { ...settings }
        settingsNext.sampleRate = currSettings.sampleRate
        settingsNext.receiverBufferSizeMs = currSettings.receiverBufferSizeMs
        settingsNext.networkPacketDurationMs = currSettings.networkPacketDurationMs
        settingsNext.receiverBufferMaxDriftMs = currSettings.receiverBufferMaxDriftMs

        const atrack = userStateRef.current.mediaStreams.audioInputs.find(
            (a) => a.info.deviceId === mediaSource.deviceId
        )
        if (atrack) {
            updateSelectedAudioDeviceProperties(atrack.info.deviceId, {
                ...settingsNext,
            })
        } else {
            setAudioInputSettings(mediaSource.deviceId, settingsNext)
        }
    }

    const onRemoveDeviceClick = () => {
        toggleAudioDeviceSelection(mediaSource)
        onClose()
    }

    const onGuideLinkClick = () => {
        ElectronIPC.openLinkInBrowser(Constants.links.muse_pro_guide)
    }

    return (
        <div className="w-full h-full flex flex-col justify-between">
            <DialogBody>
                <div className="flex items-center justify-between pr-12">
                    <h2 className="t-title-dialog">{t('Device Settings')}</h2>
                    <div className="flex items-center gap-2">
                        <InternalGuideIcon />
                        <Button variant="link" onClick={onGuideLinkClick}>
                            Guide
                        </Button>
                    </div>
                </div>

                <div className="flex flex-col gap-[20px] pb-[16px]">
                    <div className="t-subtitle-dialog pt-2">{mediaSource.displayLabel || '-'}</div>

                    <ChannelSection mediaSelection={mediaSelection} currSettings={settings} atrack={atrack} />

                    <div>
                        <AudioQualitySelect
                            label={t('Quality')}
                            value={currQualityPresetCode}
                            onChange={handleChangeQuality}
                            widthMatching
                        />
                        <QualitySelectionDescriptionSection mediaSelection={mediaSelection} />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <SampleRateSelectionSection
                            mediaSelection={mediaSelection}
                            atrack={atrack}
                            currSettings={currSettings}
                            setSettingsHelper={setSettingsHelper}
                        />

                        <LatencySelectionSection
                            mediaSelection={mediaSelection}
                            currSettings={currSettings}
                            setSettingsHelper={setSettingsHelper}
                        />
                    </div>

                    {mediaSelection.source.isMuseAudioShare && (
                        <LoopbackToggleSection mediaSelection={mediaSelection} />
                    )}

                    {showAdvanced && (
                        <div className="grid grid-cols-2 gap-2">
                            <MaxDriftSelectionSection
                                currSettings={currSettings}
                                setSettingsHelper={setSettingsHelper}
                            />
                            <PacketSizeSelectionSection
                                currSettings={currSettings}
                                setSettingsHelper={setSettingsHelper}
                            />
                        </div>
                    )}
                </div>
            </DialogBody>

            <DialogActionButtonsSection
                leftButtonText={t('Remove device')}
                onLeftButtonClicked={onRemoveDeviceClick}
                rightButtonText={t('Done')}
                onRightButtonClicked={() => {
                    onSubmit()
                    onClose()
                }}
            />
        </div>
    )
}
